import type { DirectorPlayerProfile, DirectorPoolProfile, MetaResponse } from '@league/shared/contract';
import type { PlayingPace } from '@league/shared/schemas';
import { LEAGUE_STATUS } from '@league/shared/status';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Trophy, UsersRound } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { PaceChoices } from '../../components/PlayingPreferences';
import { Button, ErrorNote, Field, Input } from '../../components/ui';
import { paceLabel } from '../../content/playing-preferences';
import { ApiError, apiFetch, apiPatch } from '../../lib/api';
import type { Actor } from '../../lib/auth';
import { Stat } from '../player/YouPage';
import '../player/player.css';
import '../player/profile.css';

/**
 * A PLAYER, AS STAFF SEE THEM (2026-10-26). Built from the same parts as the player's own profile —
 * the navy rating card, the three outline numbers, league cards — so the director and the player
 * look at one person the same way. One Edit for everything about them; the commissioner alone can
 * delete them.
 */
export function PlayerProfile({ actor }: { actor: Actor }) {
  const { playerId = '' } = useParams();
  const { pathname } = useLocation();
  const profile = useQuery({
    queryKey: ['director-player', playerId],
    queryFn: async (): Promise<DirectorPlayerProfile | DirectorPoolProfile> => {
      try { return await apiFetch<DirectorPlayerProfile>(`/api/players/${playerId}/profile`); }
      catch (error) {
        if (!(error instanceof ApiError) || error.status !== 404) throw error;
        return apiFetch<DirectorPoolProfile>(`/api/players/${playerId}/pool-profile`);
      }
    },
  });
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => apiFetch<MetaResponse>('/api/meta') });
  const brand = meta.data?.brand ?? 'League';
  const backTo = pathname.startsWith('/hq') ? '/hq/players' : '/director/players';
  const data = profile.data;

  return (
    <main className="player-page profile-dashboard">
      <Link to={backTo} className="self-start text-sm underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4">← Players</Link>
      {profile.isPending ? (
        <p role="status">Loading player…</p>
      ) : profile.isError || !data ? (
        <ErrorNote>Could not load this player. They may not be available in your leagues.</ErrorNote>
      ) : 'kind' in data ? (
        <PoolDetails data={data} />
      ) : (
        <>
          <Details player={data.player} />

          <section className="profile-rating">
            <div className="profile-rating-copy">
              <p>{brand} rating</p>
              <p className={`profile-rating-number tabular${data.record?.rating.performancePercentile == null ? ' profile-rating-empty' : ''}`}>
                {data.record?.rating.performancePercentile?.toFixed(1) ?? 'Not yet rated'}
              </p>
              {data.record && data.record.rating.status !== 'unrated' ? (
                <p className="profile-rating-status">
                  {data.record.rating.status === 'established'
                    ? `Established · ${data.record.rating.sessions} sessions`
                    : `Getting started · ${data.record.rating.sessions} of 4 sessions`}
                </p>
              ) : null}
            </div>
          </section>

          <div className="profile-stats">
            <Stat label="Leagues played" value={data.leaguesPlayed} icon={<UsersRound />} />
            <Stat label="Sessions played" value={data.sessionsPlayed} icon={<CalendarDays />} />
            <Stat
              label="Hands won"
              value={data.record?.career.handsWonVerified ? data.record.career.handsWon : null}
              icon={<Trophy />}
            />
          </div>

          <section className="profile-card profile-leagues">
            <h2>League history</h2>
            <p className="text-sm text-ink-2">Leagues you have access to.</p>
            {data.leagues.length ? (
              <ul className="mt-2 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
                {data.leagues.map((league) => (
                  <li key={league.id} className="flex items-baseline justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <Link
                        className="break-words font-display text-[22px] underline-offset-4 hover:underline focus-visible:outline-2"
                        to={`/director/leagues/${league.id}`}
                      >
                        {league.name}
                      </Link>
                      <p className="text-[13px] text-ink-2">
                        {league.area} · {league.sessionsPlayed} {league.sessionsPlayed === 1 ? 'session' : 'sessions'} played
                      </p>
                      {league.enrollmentStatus !== 'enrolled' ? (
                        <p className="text-[13px] text-ink-2">
                          {league.enrollmentStatus === 'waitlist' ? 'Waitlist' : league.enrollmentStatus === 'dropped' ? 'No longer enrolled' : 'Registration awaiting confirmation'}
                        </p>
                      ) : null}
                      {league.paceOverride ? <p className="mt-1 text-[13px]">Director pace adjustment: {paceLabel(league.paceOverride)}</p> : null}
                    </div>
                    <span className="shrink-0 text-[12px] text-ink-3">{LEAGUE_STATUS[league.status].label}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-2">No league history available.</p>
            )}
          </section>

          {actor.role === 'commissioner' ? <DeletePlayer player={data.player} after={backTo} /> : null}
        </>
      )}
    </main>
  );
}

/** The person: name as the heading, then one card with everything about how to reach them and one Edit. */
function Details({ player }: { player: DirectorPlayerProfile['player'] }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  return (
    <>
      <header className="profile-welcome">
        <div>
          <h1>{player.firstName} {player.lastName}</h1>
        </div>
      </header>
      <section className="profile-card">
        {editing ? (
          <EditForm
            key={player.id}
            player={player}
            onCancel={() => setEditing(false)}
            onSaved={() => { setEditing(false); setSaved(true); }}
          />
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-4">
            <dl className="grid flex-1 gap-4 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-ink-3">Email</dt>
                <dd className="break-words">{player.email ?? 'No email'}</dd>
                {player.email && !player.emailEditable ? <dd className="mt-1 text-xs text-ink-3">This is their director or instructor sign-in, so it can’t be changed here.</dd> : null}
              </div>
              <div><dt className="text-ink-3">Phone</dt><dd>{player.phone || 'Not provided'}</dd></div>
              <div><dt className="text-ink-3">Player’s preferred pace</dt><dd>{paceLabel(player.preferredPace)}</dd></div>
            </dl>
            <Button variant="secondary" onClick={() => { setSaved(false); setEditing(true); }}>Edit details</Button>
          </div>
        )}
        {saved ? <p role="status" className="mt-3 text-sm text-ink-2">Contact information saved.</p> : null}
      </section>
    </>
  );
}

/**
 * EVERYTHING ABOUT THEM IN ONE FORM. Three endpoints sit behind it — contact, email, pace — and only
 * the ones that changed are called, so saving a phone number never touches an email.
 */
function EditForm({ player, onCancel, onSaved }: { player: DirectorPlayerProfile['player']; onCancel: () => void; onSaved: () => void }) {
  const client = useQueryClient();
  const [firstName, setFirstName] = useState(player.firstName);
  const [lastName, setLastName] = useState(player.lastName);
  const [email, setEmail] = useState(player.email ?? '');
  const [phone, setPhone] = useState(player.phone ?? '');
  const [pace, setPace] = useState<PlayingPace | null>(player.preferredPace);
  const [expectedUpdatedAt] = useState(player.updatedAt);
  const save = useMutation({
    mutationFn: async () => {
      if (firstName.trim() !== player.firstName || lastName.trim() !== player.lastName || (phone.trim() || null) !== (player.phone || null)) {
        await apiFetch(`/api/players/${player.id}/contact`, {
          method: 'PATCH',
          body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() || null, expectedUpdatedAt }),
        });
      }
      if (player.emailEditable && email.trim() && email.trim() !== (player.email ?? '')) {
        await apiPatch(`/api/players/${player.id}/email`, { email: email.trim() });
      }
      if (pace !== player.preferredPace) {
        await apiFetch(`/api/players/${player.id}/preferences`, { method: 'PATCH', body: JSON.stringify({ preferredPace: pace }) });
      }
    },
    onSuccess: async () => { await client.invalidateQueries(); onSaved(); },
  });
  return (
    <form className="flex flex-col gap-4" onSubmit={(event) => { event.preventDefault(); if (!save.isPending) save.mutate(); }}>
      <p className="text-sm text-ink-2">Changes made here to the player’s name or phone will apply across all of their leagues.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" htmlFor="profile-first"><Input id="profile-first" required maxLength={120} value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={save.isPending} /></Field>
        <Field label="Last name" htmlFor="profile-last"><Input id="profile-last" required maxLength={120} value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={save.isPending} /></Field>
        {player.emailEditable ? (
          <Field label="Email" htmlFor="profile-email"><Input id="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={save.isPending} /></Field>
        ) : null}
        <Field label="Phone · Optional" htmlFor="profile-phone"><Input id="profile-phone" type="tel" maxLength={60} value={phone} onChange={(e) => setPhone(e.target.value)} disabled={save.isPending} /></Field>
      </div>
      <PaceChoices value={pace} onChange={setPace} disabled={save.isPending} />
      {save.isError ? <ErrorNote>{save.error instanceof ApiError ? save.error.message : 'Could not save those changes.'}</ErrorNote> : null}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={save.isPending || !firstName.trim() || !lastName.trim()}>{save.isPending ? 'Saving…' : 'Save changes'}</Button>
        <Button type="button" variant="secondary" disabled={save.isPending} onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

/** Commissioner only (owner, 2026-10-26). The player becomes "Former player"; their scores stay. */
function DeletePlayer({ player, after }: { player: DirectorPlayerProfile['player']; after: string }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const confirm = useRef<HTMLDialogElement>(null);
  const name = `${player.firstName} ${player.lastName}`;
  const remove = useMutation({
    mutationFn: () => apiFetch(`/api/players/${player.id}`, { method: 'DELETE' }),
    onSuccess: async () => { confirm.current?.close(); await client.invalidateQueries(); navigate(after, { replace: true }); },
  });
  return (
    <section className="flex justify-end">
      <Button variant="destructive" onClick={() => { remove.reset(); confirm.current?.showModal(); }}>Delete player</Button>
      <dialog
        ref={confirm}
        aria-labelledby="delete-player-title"
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-md border border-light bg-paper p-6 text-ink backdrop:bg-black/30"
        onCancel={(event) => { if (remove.isPending) event.preventDefault(); }}
      >
        <h2 id="delete-player-title" className="font-display text-xl">Delete {name}?</h2>
        <p className="mt-2 text-sm text-ink-2">
          Their name, email, phone and sign-in are removed. Their scores stay, shown as “Former player”. This can’t be undone.
        </p>
        {remove.error ? <ErrorNote>{remove.error instanceof ApiError ? remove.error.message : 'Could not delete this player.'}</ErrorNote> : null}
        <div className="mt-5 flex gap-3">
          <Button autoFocus variant="secondary" disabled={remove.isPending} onClick={() => confirm.current?.close()}>Cancel</Button>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Deleting…' : 'Delete player'}
          </Button>
        </div>
      </dialog>
    </section>
  );
}

function PoolDetails({ data }: { data: DirectorPoolProfile }) {
  return (
    <>
      <header className="profile-welcome">
        <div>
          <h1>{data.player.firstName} {data.player.lastName}</h1>
        </div>
      </header>
      <section className="profile-card">
        <dl className="grid gap-4 text-sm sm:grid-cols-3">
          <div><dt className="text-ink-3">Email</dt><dd className="break-words">{data.player.email ?? 'No email'}</dd></div>
          <div><dt className="text-ink-3">Phone</dt><dd>{data.player.phone || 'Not provided'}</dd></div>
          <div><dt className="text-ink-3">Player’s preferred pace</dt><dd>{paceLabel(data.player.preferredPace)}</dd></div>
        </dl>
      </section>
      <section className="profile-card">
        <h2>Assignments</h2>
        {data.assignments.length ? (
          <ul className="mt-2 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
            {data.assignments.map((assignment) => (
              <li key={assignment.sessionId} className="px-4 py-3">
                <Link className="underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4" to={`/director/sessions/${assignment.sessionId}/seating`}>
                  {assignment.leagueName} · Session {assignment.sessionNumber}
                </Link>
                {assignment.paceOverride ? <p className="mt-1 text-[13px]">Director pace adjustment: {paceLabel(assignment.paceOverride)}</p> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-2">No assignments in your leagues.</p>
        )}
      </section>
    </>
  );
}
